import React from 'react';
import { AuthBadRequestError, isValidEmail } from '@creditas-lab/auth-sdk';
import { useStep } from '../../../components/StepWizard';
import {
  useRequestClaimAddition,
  RequestClaimAdditionResponse,
} from '../../../hooks/mutations/useRequestClaimAddition';
import i18n from '../../../i18n';
import {
  dispatchAddEmailExistingEmailEvent,
  dispatchAddEmailNextStepEvent,
  dispatchRequestClaimAdditionErrorEvent,
} from '../../../utils/analyticsEvents';
import { FlowTypes } from '../enums/FlowTypes';
import { useGetFlowValues } from '../useGetFlowValues';

export type UseEmailStepProps = {
  handleChange: (ev: React.FormEvent<HTMLInputElement>) => void;
  handleSubmit: (
    event: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>,
  ) => void;
  isValid: boolean;
  email: string;
  errorMessage: string;
};

const useEmailStep = (flowType: FlowTypes): UseEmailStepProps => {
  const { setData } = useGetFlowValues(flowType);
  const [email, setEmail] = React.useState<string>('');
  const [isValid, setValid] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const { handleNext } = useStep();

  const requestClaimAdditionMutation = useRequestClaimAddition({
    onSuccess: ({ verificationTicket }: RequestClaimAdditionResponse) => {
      setData({ claim: email, verificationTicket });
      dispatchAddEmailNextStepEvent();
      handleNext();
    },
    onError: (error: Error): void => {
      if (error instanceof AuthBadRequestError) {
        dispatchAddEmailExistingEmailEvent();
        setErrorMessage(i18n.t('flows:add_email.email_step.validations.invalid_email'));
        return;
      }

      dispatchRequestClaimAdditionErrorEvent(`${FlowTypes.EMAIL} | ${error.message}`);
      setErrorMessage(i18n.t('flows:add_email.email_step.validations.unexpected_error'));
    },
  });

  const handleChange = (ev: React.FormEvent<HTMLInputElement>): void => {
    const { value } = ev.currentTarget;
    setValid(isValidEmail(value));
    setEmail(value);
  };

  const handleSubmit = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>,
  ): void => {
    event.preventDefault();
    requestClaimAdditionMutation.mutate(email);
  };

  return { handleChange, handleSubmit, email, isValid, errorMessage };
};

export { useEmailStep };
